import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { LanguageModeService } from "@app/shared/services/language-mode.service";
import { ActiveLanguage } from "@app/shared/services/language-config.store";
import {
  EnrichmentLocale,
  LocaleMap,
  LocalizedText,
  resolveLocalized,
} from "@app/shared/models/enrichment-detail.model";

/**
 * Inline locale-map editor used throughout the Pricing tab. Signal-based
 * (no FormGroup ceremony). Echoes the legacy `MultilingualFieldGroup`
 * visual: 2-column responsive grid of locale cells, a per-locale
 * "copy to the other languages" action, optional tip line.
 *
 * Tolerant on input: accepts `LocaleMap`, plain string (mirrored across
 * every locale), or null/undefined (blanks).
 */
@Component({
  selector: "pricing-locale-input",
  standalone: true,
  imports: [CommonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./pricing-locale-input.html",
  styleUrls: ["./pricing-locale-input.scss"],
})
export class PricingLocaleInput {
  /** Editor-wide language view filter — when a hero card is selected
   *  (e.g. "EN") this service emits the corresponding shortCode and every
   *  locale input collapses to that single language only. "ALL" shows
   *  every active locale. */
  private readonly languageMode = inject(LanguageModeService);

  /** Current value — string, locale-map, null, or undefined. */
  readonly value = input<LocalizedText>(undefined);
  /** Active language set (defaults to whatever the parent passes). */
  readonly languages = input.required<ActiveLanguage[]>();
  /** `input` for one-line strings; `textarea` for descriptions / notes. */
  readonly type = input<"input" | "textarea">("input");
  /** Default row count for textareas — generous enough to read multi-
   *  line marketing copy without scrolling but still resizable. */
  readonly rows = input<number>(5);
  /** Placeholder shown when the locale's value is empty. */
  readonly placeholder = input<string>("");
  /** Disable all inputs (e.g. read-only price). */
  readonly disabled = input<boolean>(false);
  /** Optional helper text shown under the grid (e.g. character limit). */
  readonly tip = input<string>("");
  /** Required-field mode: render EVERY empty active-locale cell red (a
   *  readiness must-fix — e.g. the price name, which is required, so any
   *  empty locale is a blocker). Opt-in per field so structural editors
   *  don't flag. */
  readonly flagMissing = input<boolean>(false);

  /** Language-gap mode: render an empty cell red only when another active
   *  locale already has a value — i.e. a partial translation. An entirely
   *  empty optional field (description, notes…) is left alone, so we don't
   *  shout at fields the user simply hasn't started. */
  readonly flagGaps = input<boolean>(false);

  /** Emitted on every keystroke — the full updated locale map. */
  readonly valueChange = output<LocaleMap>();

  /** Languages actually rendered as inputs. When the editor-wide
   *  language filter is "ALL" (or our `disableViewFilter` opts out) we
   *  render the full active set; when set to a specific shortCode
   *  (TH/EN/MM/KH/etc) we collapse to that single language only — this
   *  is what the hero language-coverage cards trigger when clicked. */
  readonly visibleLanguages = computed<ActiveLanguage[]>(() => {
    const all = this.languages();
    if (this.disableViewFilter()) return all;
    const view = this.languageMode.active();
    if (view === "ALL") return all;
    const target = String(view).toUpperCase();
    const filtered = all.filter(
      (l) => (l.shortCode || l.label || "").toUpperCase() === target,
    );
    // Unknown view code (legacy "MY" alias for MM, or a code that
    // doesn't exist in the active set) → fall back to showing everything
    // rather than rendering an empty grid.
    return filtered.length ? filtered : all;
  });

  /** Opt-out — for editors where the editor-wide language filter doesn't
   *  make sense and every active locale must stay on screen at once. */
  readonly disableViewFilter = input<boolean>(false);

  /** Resolve the input value to a LocaleMap so the template renders
   *  consistently regardless of whether the parent passed a string or a
   *  partial map. Plain strings are mirrored into every active locale so
   *  edits start from the same baseline across languages. */
  readonly resolved = computed<LocaleMap>(() => {
    const raw = this.value();
    if (raw == null) return {};
    if (typeof raw === "string") {
      const map: Record<string, string> = {};
      for (const lang of this.languages()) map[lang.locale] = raw;
      return map as unknown as LocaleMap;
    }
    return raw as LocaleMap;
  });

  /** Read a single locale's value, defaulting to empty string. */
  readValue(locale: string): string {
    const v = this.resolved()[locale as EnrichmentLocale];
    return v ?? "";
  }

  /** True when a locale has any non-empty value — drives the filled
   *  dot indicator next to each locale tag. */
  hasValue(locale: string): boolean {
    return this.readValue(locale).trim().length > 0;
  }

  /** True when this locale's cell should render as an error. Disabled
   *  (read-only) inputs never flag. Required fields ({@link flagMissing})
   *  flag any empty cell; gap fields ({@link flagGaps}) flag an empty cell
   *  only when a sibling locale is filled (a partial translation). */
  isMissing(locale: string): boolean {
    if (this.disabled()) return false;
    if (this.hasValue(locale)) return false;
    if (this.flagMissing()) return true;
    return this.flagGaps() && this.anySiblingFilled(locale);
  }

  /** True when some OTHER active locale already has a value — used to tell a
   *  partial-translation gap apart from an untouched (all-empty) field. */
  private anySiblingFilled(locale: string): boolean {
    return this.languages().some(
      (l) => l.locale !== locale && this.hasValue(l.locale),
    );
  }

  /** Write a locale and emit the full updated map. */
  writeValue(locale: string, next: string): void {
    const cur = this.resolved();
    const map: LocaleMap = { ...cur, [locale as EnrichmentLocale]: next };
    this.valueChange.emit(map);
  }

  /** Thai is treated as a primary, authored-by-hand content language —
   *  never an automatic copy target. Marketing always writes TH from
   *  scratch (it carries brand voice + nuance that machine-copying any
   *  other language would destroy). */
  private isThai(l: ActiveLanguage): boolean {
    return l.locale === "th-TH" || l.label === "TH" || l.shortCode === "TH";
  }

  private isLocale(l: ActiveLanguage, locale: string): boolean {
    return l.locale === locale;
  }

  /** Targets a fanout from `sourceLocale` would write to. Always
   *  excludes the source itself + Thai (see {@link isThai}). */
  private fanoutTargets(sourceLocale: string): ActiveLanguage[] {
    return this.languages().filter(
      (l) => !this.isLocale(l, sourceLocale) && !this.isThai(l),
    );
  }

  /** True when fanning out from this locale would actually write
   *  somewhere — drives whether the `↗` button renders. Always false
   *  when the source itself is empty (nothing to copy) or when the
   *  only other active locale is Thai (which is excluded). */
  canFanout(sourceLocale: string): boolean {
    if (!this.hasValue(sourceLocale)) return false;
    return this.fanoutTargets(sourceLocale).length > 0;
  }

  /** Copy a locale's value across every OTHER active locale (Thai
   *  excepted — see {@link isThai}). Replaces the legacy "Copy EN →
   *  others" toolbar button — fanning out from any locale (not just
   *  English) is more flexible and lives next to each input instead of
   *  floating above the grid where it cluttered the header. */
  copyFromLocaleToOthers(sourceLocale: string): void {
    const cur = { ...this.resolved() };
    const sourceValue = cur[sourceLocale as EnrichmentLocale];
    if (sourceValue == null || String(sourceValue).trim().length === 0) return;
    for (const l of this.fanoutTargets(sourceLocale)) {
      cur[l.locale as EnrichmentLocale] = sourceValue;
    }
    this.valueChange.emit(cur);
  }

  /** Tooltip text for the per-locale fanout button — lists every target
   *  locale so marketing sees what will be overwritten, and flags the
   *  TH exclusion when relevant. */
  fanoutTooltip(sourceLocale: string): string {
    const targets = this.fanoutTargets(sourceLocale)
      .map((l) => l.shortCode || l.label)
      .filter(Boolean);
    if (targets.length === 0) return "";
    const hasThaiInScope = this.languages().some((l) => this.isThai(l));
    const tail = hasThaiInScope ? " · Thai is never overwritten" : "";
    return `Copy this value to ${targets.join(", ")}${tail}`;
  }

  /** Best-effort single-string fallback — used when the parent collapses
   *  to a non-localized display elsewhere. */
  resolveSingle(): string {
    return resolveLocalized(this.value());
  }

  trackByLocale = (_: number, l: ActiveLanguage): string => l.locale;
}
